import React, { useState } from 'react'
import useSWR from 'swr'
import { formatRelative, parseISO } from 'date-fns'
import {
  FormControl,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import Linkify from 'linkify-react/dist/linkify-react.cjs'
import clsx from 'clsx'

import { TextButton } from '~/legacy/components/buttons'
import { getTokensFromLocalStorage } from '~/legacy/utils/localStorageHelper'
import Loading from '~/legacy/components/Loading/Loading'
import { Typography } from '~/legacy/components'
import { getPlural } from '~/legacy/utils'

export function BrokerNotesSection({ surveyBuilding }) {
  const [note, setNote] = useState('')
  const [isMutating, setIsMutating] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editingText, setEditingText] = useState('')
  const [itemMutatingId, setItemMutatingId] = useState(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [menuNoteId, setMenuNoteId] = useState(null)
  const [isRevealed, setIsRevealed] = useState(false)

  const {
    data: brokerNotes,
    error,
    mutate,
  } = useSWR(`broker-notes/${surveyBuilding.id}`, () => {
    const token = getTokensFromLocalStorage()?.access
    const headers = token ? { Authorization: `Bearer ${token}` } : {}

    return fetch(
      `/api/survey-buildings/${surveyBuilding.id}/broker-notes/list`,
      {
        method: 'POST',
        headers,
      }
    ).then((res) => res.json())
  })

  const handleSubmit = async () => {
    if (isMutating || !note) return

    setIsMutating(true)
    const token = getTokensFromLocalStorage()?.access
    const headers = token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      : {}

    try {
      await fetch(
        `/api/survey-buildings/${surveyBuilding.id}/broker-notes/create`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ text: note }),
        }
      )
      setNote('')
      mutate()
    } catch (error) {
      console.error(error)
    } finally {
      setIsMutating(false)
    }
  }

  const handleDelete = async (noteId) => {
    if (itemMutatingId) return

    setItemMutatingId(noteId)
    const token = getTokensFromLocalStorage()?.access
    const headers = token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      : {}

    try {
      await fetch(
        `/api/survey-buildings/${surveyBuilding.id}/broker-notes/delete`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ id: surveyBuilding.id, noteId }),
        }
      )
      mutate()
    } catch (error) {
      console.error(error)
    } finally {
      setItemMutatingId(null)
    }
  }

  const startEdit = (n) => {
    setEditingId(n.id)
    setEditingText(n.text)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingText('')
  }

  const saveEdit = async () => {
    if (!editingId || !editingText || itemMutatingId) return

    setItemMutatingId(editingId)
    const token = getTokensFromLocalStorage()?.access
    const headers = token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      : {}

    try {
      await fetch(
        `/api/survey-buildings/${surveyBuilding.id}/broker-notes/update`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            id: surveyBuilding.id,
            noteId: editingId,
            text: editingText,
          }),
        }
      )
      cancelEdit()
      mutate()
    } catch (error) {
      console.error(error)
    } finally {
      setItemMutatingId(null)
    }
  }

  const openMenu = (event, noteId) => {
    setMenuAnchorEl(event.currentTarget)
    setMenuNoteId(noteId)
  }

  const closeMenu = () => {
    setMenuAnchorEl(null)
    setMenuNoteId(null)
  }

  if (error) {
    return (
      <div className="py-10 text-sm">
        Error:
        {error.message}
      </div>
    )
  }

  if (!brokerNotes) {
    return <div className="py-10 text-sm">Loading notes...</div>
  }

  return (
    <div className="py-10 flex flex-col gap-6">
      <FormControl fullWidth>
        <OutlinedInput
          placeholder="Add a note"
          type="text"
          multiline
          value={note}
          disabled={isMutating}
          onChange={(e) => setNote(e.target.value)}
          className="h-fit"
          endAdornment={
            <InputAdornment position="end">
              <TextButton
                color="primary"
                onClick={handleSubmit}
                type="button"
                disabled={isMutating}
              >
                Add
              </TextButton>
              <Loading size={24} isLoading={isMutating} />
            </InputAdornment>
          }
        />
      </FormControl>

      <div>
        {brokerNotes.length > 0 && (
          <div className="text-lg font-semibold mb-2">
            <Typography variant="boldText">
              {getPlural(brokerNotes.length, 'Note')}
            </Typography>
          </div>
        )}

        {brokerNotes.length > 0 && !isRevealed && (
          <button
            type="button"
            onClick={() => setIsRevealed(true)}
            className="text-left p-4 border border-solid border-[#E0E0E0] rounded-md bg-gray-100 hover:bg-gray-200 text-base cursor-pointer"
          >
            Notes are hidden by default to prevent accidentally showing private
            information. Click here to reveal them.
          </button>
        )}

        {brokerNotes.length > 0 && isRevealed && (
          <div className="flex flex-col gap-2">
            {brokerNotes.map((n) => (
              <BrokerNoteCard
                key={n.id}
                note={n}
                isEditing={editingId === n.id}
                editingText={editingText}
                onChangeEditingText={setEditingText}
                onCancelEdit={cancelEdit}
                onSaveEdit={saveEdit}
                onOpenMenu={(e) => openMenu(e, n.id)}
                isMutating={isMutating}
                itemMutatingId={itemMutatingId}
              />
            ))}
          </div>
        )}
      </div>

      <Menu
        anchorEl={menuAnchorEl}
        keepMounted
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => {
            const current = brokerNotes.find((bn) => bn.id === menuNoteId)
            if (current) startEdit(current)
            closeMenu()
          }}
          disabled={isMutating || !!itemMutatingId}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (editingId) return
            handleDelete(menuNoteId)
            closeMenu()
          }}
          disabled={isMutating || !!itemMutatingId || !!editingId}
        >
          Delete
        </MenuItem>
      </Menu>
    </div>
  )
}

function BrokerNoteCard({
  note,
  isEditing,
  editingText,
  onChangeEditingText,
  onCancelEdit,
  onSaveEdit,
  onOpenMenu,
  isMutating,
  itemMutatingId,
}) {
  const theme = useTheme()

  return (
    <div
      className={clsx(
        'whitespace-pre-line p-4 border border-solid border-[#E0E0E0] rounded-md bg-white shadow-sm',
        isMutating && 'animate-pulse'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs text-[#666666] font-medium">
          Last updated: 
          {' '}
          {formatRelative(parseISO(note.updated_at), new Date())}
        </div>
        {!isEditing && (
          <IconButton
            aria-label="note actions"
            onClick={onOpenMenu}
            disabled={isMutating || !!itemMutatingId}
            size="small"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </div>

      {isEditing ? (
        <div className="mt-2">
          <OutlinedInput
            fullWidth
            multiline
            value={editingText}
            onChange={(e) => onChangeEditingText(e.target.value)}
            className="h-fit"
          />
          <div className="flex items-center justify-end gap-2 mt-2">
            <TextButton
              type="button"
              disabled={itemMutatingId === note.id}
              onClick={onCancelEdit}
            >
              Cancel
            </TextButton>

            <TextButton
              color="primary"
              type="button"
              disabled={itemMutatingId === note.id}
              onClick={onSaveEdit}
            >
              Save
            </TextButton>

            <Loading size={20} isLoading={itemMutatingId === note.id} />
          </div>
        </div>
      ) : (
        <div className="mt-2 text-sm m-0">
          <Linkify
            options={{
              target: '_blank',
              rel: 'noopener',
              render: ({ attributes, content }) => {
                const { href, ...props } = attributes
                return (
                  <a
                    href={href}
                    {...props}
                    style={{ color: theme.palette.primary.main }}
                  >
                    {content}
                  </a>
                )
              },
            }}
          >
            <p className="m-0 text-sm">{note.text}</p>
          </Linkify>
        </div>
      )}
    </div>
  )
}
